import { ReactNode, useEffect, useState, useRef } from 'react';
import { TeamLogo } from '../TeamLogo/TeamLogo';
import { PushNotificationToggle } from './PushNotificationToggle';
import LnhNewsSection from '../League/LnhNewsSection';
import { translate } from '../../lib/translations';
import { isTslhTenant } from '../../lib/tenantStore';
import {
  compactCurrency,
  getDaysUntil,
  getPoints,
  getRelativeDateLabel,
  longDate,
} from './myOfficeShared';
import type { StandingRow, TodoItem, TradePreview, UpcomingEventItem } from './myOfficeShared';
import './MyOfficeEmbed.css';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { getLiveStandings, getDailyStandingsWindow, getStandingsWindow } from '../../lib/api/standings';
import { getLeagueSettings } from '../../lib/api/leagueSettings';

// Typos Arsenal Sports (Anton pour les titres, Open Sans pour le texte).
// Chargées uniquement lorsque le rendu embed est affiché.
const ARSENAL_FONTS_ID = 'dp-arsenal-embed-fonts';
const ARSENAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts() {
  useEffect(() => {
    if (document.getElementById(ARSENAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = ARSENAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

export interface LineupCountsLite {
  forwards: number;
  defenders: number;
  goalies: number;
  reserveTotal: number;
}

export interface MyOfficeEmbedProps {
  language: 'fr' | 'en';
  leagueName: string;
  teamName: string;
  teamLogoAbbrev: string | null;
  teamStatus: string;
  currentRow: StandingRow | null;
  nearbyStandings: StandingRow[];
  mainCap: number;
  maxSalaryCap: number;
  availableMainCap: number;
  lineupCounts: LineupCountsLite | null;
  requiredLineup: { forwards: number; defenders: number; goalies: number; reserve: number };
  ltirCount: number;
  injuryLimit: number;
  injuredCount: number;
  signingsUsed: number;
  maxSignings: number;
  todoItems: TodoItem[];
  upcomingEvents: UpcomingEventItem[];
  pendingTradesReceived: number;
  pendingTradesSent: number;
  tradePreviewsReceived: TradePreview[];
  tradePreviewsSent: TradePreview[];
  isStandardFlexible: boolean;
  isBuildPhase: boolean;
  isBpmSession: boolean;
  isCommissioner: boolean;
  showLeaveModal: boolean;
  isLeavingLeague: boolean;
  onOpenLeaveModal: () => void;
  onCloseLeaveModal: () => void;
  onConfirmLeave: () => void;
  onNavigate: (path: string) => void;
}

function Section({
  title,
  count,
  action,
  children,
}: {
  title?: string;
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="my-office-embed__section">
      {(title || action) && (
        <div className="my-office-embed__section-head">
          {title && (
            <h2 className="my-office-embed__section-title">
              {title}
              {count != null && <span className="my-office-embed__section-count"> {count}</span>}
            </h2>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

function Stat({
  label,
  value,
  sub,
  danger,
  children,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  danger?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="moe-stat">
      <div className="moe-stat__label">{label}</div>
      <div className={`moe-stat__value${danger ? ' moe-stat__value--danger' : ''}`}>{value}</div>
      {sub && <div className="moe-stat__sub">{sub}</div>}
      {children}
    </div>
  );
}

function sumRowPoints(row: any) {
  if (!row) return 0;
  return (
    (Number(row.totgoals) || 0) +
    (Number(row.totassists) || 0) +
    (Number(row.totgoals_goalie) || 0) +
    (Number(row.totassists_goalie) || 0) +
    (Number(row.totwins_goalie) || 0) +
    (Number(row.totshutout_goalie) || 0) +
    (Number(row.tototlosses_goalie) || 0)
  );
}

function InfoRapide({ language, currentRow }: { language: 'fr' | 'en'; currentRow: StandingRow | null }) {
  const league = useLeagueStore((s) => s.selectedLeague);
  const leagueId = league?.id;
  const userId = useUserStore((s) => s.user_id);

  const [pointsTotal, setPointsTotal] = useState<number | null>(currentRow ? getPoints(currentRow) : null);
  const [pointsYesterday, setPointsYesterday] = useState<number | null>(null);
  const [pointsLive, setPointsLive] = useState<number | null>(null);
  const [position, setPosition] = useState<number | null>(currentRow?.rank ?? null);
  const [loading, setLoading] = useState(false);

  const liveIntervalRef = useRef<number | null>(null);

  const fetchLive = async (ptSettings?: any) => {
    if (!leagueId || !userId) return;
    try {
      const settings = ptSettings ?? (await getLeagueSettings(leagueId)).data;
      const pointSettings = settings
        ? {
            pts_goal_att: settings.pts_goal_att ?? 1,
            pts_ass_att: settings.pts_ass_att ?? 1,
            pts_goal_def: settings.pts_goal_def ?? 2,
            pts_ass_def: settings.pts_ass_def ?? 1,
            pts_goal_g: settings.pts_goal_g ?? 5,
            pts_win_g: settings.pts_win_g ?? 2,
            pts_shu_g: settings.pts_shu_g ?? 3,
            pts_otlosse_g: settings.pts_otlosse_g ?? 1,
          }
        : {
            pts_goal_att: 1,
            pts_ass_att: 1,
            pts_goal_def: 2,
            pts_ass_def: 1,
            pts_goal_g: 5,
            pts_win_g: 2,
            pts_shu_g: 3,
            pts_otlosse_g: 1,
          };

      const liveResp = await getLiveStandings(leagueId, pointSettings, [Number(userId)]);
      if (liveResp && Array.isArray(liveResp.data) && liveResp.data.length > 0) {
        const row = liveResp.data[0];
        const tot = sumRowPoints(row);
        setPointsLive(tot);
      } else {
        setPointsLive(0);
      }
    } catch (err) {
      console.warn('InfoRapide: fetchLive error', err);
      setPointsLive(0);
    }
  };

  useEffect(() => {
    let mounted = true;
    async function loadAll() {
      if (!leagueId || !userId) return;
      setLoading(true);
      try {
        // Points total & position
        if (currentRow) {
          setPointsTotal(getPoints(currentRow));
          setPosition(currentRow.rank ?? null);
        } else {
          const win = await getStandingsWindow(leagueId, Number(userId), 0, 0);
          if (win && Array.isArray(win.data) && win.data.length > 0) {
            const row = win.data[0];
            setPointsTotal(sumRowPoints(row));
            setPosition(row.rank ?? null);
          } else {
            setPointsTotal(0);
            setPosition(null);
          }
        }

        // Points yesterday
        try {
          const daily = await getDailyStandingsWindow(leagueId, Number(userId), 0, 0);
          if (daily && Array.isArray(daily.data) && daily.data.length > 0) {
            setPointsYesterday(sumRowPoints(daily.data[0]));
          } else {
            setPointsYesterday(0);
          }
        } catch (e) {
          console.warn('InfoRapide: daily fetch failed', e);
          setPointsYesterday(0);
        }

        // Initial live
        const settingsResp = await getLeagueSettings(leagueId);
        await fetchLive(settingsResp.data);

        // Start interval (6 minutes)
        if (mounted) {
          const id = window.setInterval(() => fetchLive(settingsResp.data), 360000);
          liveIntervalRef.current = id;
        }
      } catch (error) {
        console.error('InfoRapide loadAll error', error);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAll();

    return () => {
      mounted = false;
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
    };
  }, [leagueId, userId, currentRow]);

  return (
    <aside className="moe-quickinfo-box">
      <div className="moe-quickinfo-box__title">{language === 'fr' ? 'Info rapide' : 'Quick info'}</div>
      <div className="moe-quickinfo">
        <div className="moe-quickinfo__item">
          <div className="moe-quickinfo__label">{language === 'fr' ? 'Points totaux' : 'Total'}</div>
          <div className="moe-quickinfo__value">{pointsTotal ?? '—'}</div>
        </div>
        <div className="moe-quickinfo__item">
          <div className="moe-quickinfo__label">{language === 'fr' ? 'Hier' : 'Yesterday'}</div>
          <div className="moe-quickinfo__value">{pointsYesterday ?? '—'}</div>
        </div>
        <div className="moe-quickinfo__item">
          <div className="moe-quickinfo__label">Live</div>
          <div className="moe-quickinfo__value">{pointsLive ?? '—'}</div>
        </div>
        <div className="moe-quickinfo__item">
          <div className="moe-quickinfo__label">{language === 'fr' ? 'Classement' : 'Rank'}</div>
          <div className="moe-quickinfo__value">{position != null ? `#${position}` : '—'}</div>
        </div>
      </div>
    </aside>
  );
}

function EventDate({ date, language }: { date?: Date; language: 'fr' | 'en' }) {
  if (!date) {
    return (
      <div className="moe-date moe-date--tbd" aria-hidden="true">
        <span className="moe-date__day">{language === 'fr' ? 'À venir' : 'TBD'}</span>
      </div>
    );
  }
  const month = date
    .toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { month: 'short' })
    .replace('.', '');
  return (
    <div className="moe-date" aria-hidden="true">
      <span className="moe-date__day">{String(date.getDate()).padStart(2, '0')}</span>
      <span className="moe-date__month">{month}</span>
    </div>
  );
}

export function MyOfficeEmbedLoading({ language }: { language: 'fr' | 'en' }) {
  useArsenalFonts();
  return (
    <div className="my-office-embed my-office-embed--loading" role="status">
      <div className="moe-spinner" aria-hidden="true" />
      {language === 'fr' ? 'Chargement du bureau…' : 'Loading office…'}
    </div>
  );
}

export function MyOfficeEmbed({
  language,
  leagueName,
  teamName,
  teamLogoAbbrev,
  teamStatus,
  currentRow,
  nearbyStandings,
  mainCap,
  maxSalaryCap,
  availableMainCap,
  lineupCounts,
  requiredLineup,
  ltirCount,
  injuryLimit,
  injuredCount,
  signingsUsed,
  maxSignings,
  todoItems,
  upcomingEvents,
  pendingTradesReceived,
  pendingTradesSent,
  tradePreviewsReceived,
  tradePreviewsSent,
  isStandardFlexible,
  isBuildPhase,
  isBpmSession,
  isCommissioner,
  showLeaveModal,
  isLeavingLeague,
  onOpenLeaveModal,
  onCloseLeaveModal,
  onConfirmLeave,
  onNavigate,
}: MyOfficeEmbedProps) {
  useArsenalFonts();

  const fr = language === 'fr';
  const locale = fr ? 'fr-CA' : 'en-CA';
  const capPercent = maxSalaryCap > 0 ? Math.min(100, (mainCap / maxSalaryCap) * 100) : 0;
  const overCap = maxSalaryCap > 0 && mainCap > maxSalaryCap;

  const urgencyMetaClass = (days: number | null) => {
    if (days === null) return '';
    if (days <= 1) return ' moe-row__meta--now';
    if (days <= 3) return ' moe-row__meta--soon';
    return '';
  };

  const hasTodos = todoItems.length > 0;

  const todoSection = (
      <Section title={fr ? 'À faire' : 'To do'} count={todoItems.length}>
        {todoItems.length === 0 ? (
          <p className="moe-empty">
            {fr
              ? 'Aucune action urgente. Votre organisation est en ordre.'
              : 'No urgent actions. Your organization is in good shape.'}
          </p>
        ) : (
          <div className="moe-rows">
            {todoItems.map((item) => (
              <div key={item.key} className={`moe-row moe-row--${item.level}`}>
                <div className="moe-row__body">
                  <div className="moe-row__title">{item.title}</div>
                  <div className="moe-row__sub">{item.subtitle}</div>
                  <div className="moe-row__cta">
                    <button type="button" className="moe-link" onClick={() => onNavigate(item.path)}>
                      {item.cta}
                    </button>
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    );

  const quickAccessTabs = isStandardFlexible ? (
    <nav className="moe-tabs" aria-label={fr ? 'Accès rapide' : 'Quick access'}>
      {isBuildPhase ? (
        <>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/build-my-team')}>
            {fr ? 'Bâtir mon équipe' : 'Build my team'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/league-rules')}>
            {fr ? 'Règles/Infos Pool' : 'Custom rules'}
          </button>
          {isTslhTenant() && (
            <a
              className="moe-tab"
              href="https://100pool.ca/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {fr ? 'Guide des poolers' : 'Poolers guide'}
            </a>
          )}
        </>
      ) : (
        <>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/my-teams')}>
            {fr ? 'Mon équipe' : 'My team'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/free-agents')}>
            {fr ? 'Agents libres' : 'Free agents'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/standings')}>
            {fr ? 'Classement' : 'Standings'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/followed-poolers')}>
            {fr ? 'Poolers suivis' : 'Followed poolers'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/other-teams')}>
            {fr ? 'Autres équipes' : 'Other teams'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/line-combinations')}>
            {fr ? 'Lignes LNH' : 'NHL lines'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/league-rules')}>
            {fr ? 'Règles/Infos Pool' : 'Custom rules'}
          </button>
          <button type="button" className="moe-tab" onClick={() => onNavigate('/my-profile')}>
            {fr ? 'Modifier mon pseudo' : 'Change username'}
          </button>
          {isTslhTenant() && (
            <a
              className="moe-tab"
              href="https://100pool.ca/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {fr ? 'Guide des poolers' : 'Poolers guide'}
            </a>
          )}
        </>
      )}
    </nav>
  ) : null;

  return (
    <div className="my-office-embed">
      <header className="my-office-embed__header">
        <div className="my-office-embed__header-main">
          <div className="my-office-embed__eyebrow">{leagueName}</div>
          <h1 className="my-office-embed__title">{fr ? 'Mon bureau' : 'My office'}</h1>
        </div>
        <InfoRapide language={language} currentRow={currentRow} />
      </header>

      {quickAccessTabs}

      {/* <Section
        title={fr ? 'Mon équipe' : 'My team'}
        action={
          <button type="button" className="moe-link" onClick={() => onNavigate('/my-teams')}>
            {fr ? 'Gérer mon équipe' : 'Manage my team'}
          </button>
        }
      >
        <div className="moe-team">
          <div className="moe-team__identity">
            {teamLogoAbbrev && <TeamLogo abbrev={teamLogoAbbrev} size={56} />}
            <div>
              <div className="moe-team__name">{teamName}</div>
              <div className="moe-team__status">
                {fr ? 'Statut' : 'Status'}: {teamStatus}
              </div>
            </div>
          </div>
          <div className="moe-team__figures">
            <Stat label={fr ? 'Classement' : 'Rank'} value={`#${currentRow?.rank || '—'}`} />
            <Stat label={fr ? 'Points' : 'Points'} value={getPoints(currentRow)} />
          </div>
        </div>
      </Section> */}

      <div className={`moe-top-grid${hasTodos ? ' moe-top-grid--todos-first' : ''}`}>
      <Section title={fr ? "Aperçu de l'équipe" : 'Team overview'}>
        <div className="moe-stats">
          <Stat
            label={fr ? 'Masse salariale' : 'Salary used'}
            value={compactCurrency(mainCap)}
            danger={overCap}
            sub={
              maxSalaryCap > 0
                ? `/ ${compactCurrency(maxSalaryCap)} max`
                : fr
                ? 'Alignement principal'
                : 'Main roster'
            }
          >
            {maxSalaryCap > 0 && (
              <div
                className="moe-capbar"
                role="progressbar"
                aria-label={fr ? 'Masse salariale utilisée' : 'Salary cap used'}
                aria-valuenow={Math.round(capPercent)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={`moe-capbar__fill${overCap ? ' moe-capbar__fill--over' : ''}`}
                  style={{ width: `${capPercent}%` }}
                />
              </div>
            )}
          </Stat>
          <Stat
            label={fr ? 'Cap disponible' : 'Cap space'}
            value={compactCurrency(availableMainCap)}
            danger={availableMainCap < 0}
            sub={fr ? 'Alignement principal' : 'Main roster'}
          />
          <Stat
            label={fr ? 'Liste des blessés' : 'Injured list'}
            value={`${ltirCount} / ${injuryLimit}`}
            sub={fr ? 'Joueurs sur la liste' : 'Players on the list'}
          />
          <Stat
            label={fr ? 'Blessés (alignement)' : 'Injured (lineup)'}
            value={injuredCount}
            danger={injuredCount > 0}
            sub={fr ? 'Dans l’alignement principal' : 'In main lineup'}
          />
          <Stat
            label={fr ? 'Signatures' : 'Signings'}
            value={`${signingsUsed} / ${maxSignings}`}
            danger={maxSignings > 0 && signingsUsed >= maxSignings}
            sub={fr ? 'Agents libres' : 'Free agents'}
          />
        </div>

        {lineupCounts && (
          <>
            <div className="moe-stat__label" style={{ marginTop: '1.4rem' }}>
              {fr ? 'Alignement' : 'Lineup'}
            </div>
            <div className="moe-lineup">
              <Stat
                label={fr ? 'ATT' : 'F'}
                value={`${lineupCounts.forwards} / ${requiredLineup.forwards}`}
                danger={lineupCounts.forwards !== requiredLineup.forwards}
              />
              <Stat
                label={fr ? 'DÉF' : 'D'}
                value={`${lineupCounts.defenders} / ${requiredLineup.defenders}`}
                danger={lineupCounts.defenders !== requiredLineup.defenders}
              />
              <Stat
                label="G"
                value={`${lineupCounts.goalies} / ${requiredLineup.goalies}`}
                danger={lineupCounts.goalies !== requiredLineup.goalies}
              />
              {(requiredLineup.reserve > 0 || lineupCounts.reserveTotal > 0) && (
                <Stat
                  label={fr ? 'Réserve' : 'Reserve'}
                  value={`${lineupCounts.reserveTotal} / ${requiredLineup.reserve}`}
                  danger={lineupCounts.reserveTotal !== requiredLineup.reserve}
                />
              )}
            </div>
          </>
        )}
      </Section>
      {todoSection}
      </div>

      {upcomingEvents.length > 0 && (
          <Section title={fr ? 'Prochaines échéances' : 'Upcoming deadlines'}>
            <div className="moe-rows">
              {upcomingEvents.slice(0, 6).map((event) => {
                const days = getDaysUntil(event.date);
                return (
                  <div key={event.key} className="moe-row">
                    <EventDate date={event.date} language={language} />
                    <div className="moe-row__body">
                      <div className="moe-row__title">{event.title}</div>
                      <div className="moe-row__sub">
                        {longDate(event.date, language)} — {event.description}
                      </div>
                      {event.ctaLabel && event.ctaPath && (
                        <div className="moe-row__cta">
                          <button
                            type="button"
                            className="moe-link"
                            onClick={() => onNavigate(event.ctaPath!)}
                          >
                            {event.ctaLabel}
                          </button>
                        </div>
                      )}
                    </div>
                    <span className={`moe-row__meta${urgencyMetaClass(days)}`}>
                      {getRelativeDateLabel(event.date, language)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Section>
      )}

      {!isStandardFlexible && (
        <Section
          title={fr ? 'Mes échanges' : 'My trades'}
          count={pendingTradesReceived + pendingTradesSent}
          action={
            <button type="button" className="moe-link" onClick={() => onNavigate('/messages')}>
              {fr ? 'Voir mes échanges' : 'View my trades'}
            </button>
          }
        >
          {pendingTradesReceived === 0 && pendingTradesSent === 0 ? (
            <p className="moe-empty">
              {fr ? 'Aucune offre d’échange en attente.' : 'No pending trade offers.'}
            </p>
          ) : (
            <>
              {tradePreviewsReceived.length > 0 && (
                <div className="moe-trade-group">
                  <div className="moe-trade-group__label">
                    {fr ? 'Offres reçues' : 'Received offers'}
                  </div>
                  <div className="moe-rows">
                    {tradePreviewsReceived.map((trade) => (
                      <div key={trade.key} className="moe-row moe-row--high">
                        <div className="moe-row__body">
                          <div className="moe-row__title">{trade.counterpart}</div>
                          <div className="moe-row__sub">
                            {fr ? 'Ils offrent' : 'They offer'}: {trade.sentSummary}
                          </div>
                          <div className="moe-row__sub">
                            {fr ? 'Contre' : 'For'}: {trade.receivedSummary}
                          </div>
                          <div className="moe-row__cta">
                            <button
                              type="button"
                              className="moe-link"
                              onClick={() => onNavigate('/messages')}
                            >
                              {fr ? 'Répondre' : 'Respond'}
                            </button>
                          </div>
                        </div>
                        <span className="moe-row__meta">
                          {new Date(trade.createdAt).toLocaleDateString(locale, {
                            day: '2-digit',
                            month: 'short',
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tradePreviewsSent.length > 0 && (
                <div className="moe-trade-group">
                  <div className="moe-trade-group__label">
                    {fr ? 'Offres envoyées' : 'Sent offers'}
                  </div>
                  <div className="moe-rows">
                    {tradePreviewsSent.map((trade) => (
                      <div key={trade.key} className="moe-row">
                        <div className="moe-row__body">
                          <div className="moe-row__title">{trade.counterpart}</div>
                          <div className="moe-row__sub">
                            {fr ? 'Vous offrez' : 'You offer'}: {trade.sentSummary}
                          </div>
                          <div className="moe-row__sub">
                            {fr ? 'Contre' : 'For'}: {trade.receivedSummary}
                          </div>
                        </div>
                        <span className="moe-row__meta">
                          {new Date(trade.createdAt).toLocaleDateString(locale, {
                            day: '2-digit',
                            month: 'short',
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Section>
      )}

      <Section
        title={fr ? 'Mon classement' : 'My standings'}
        action={
          <button type="button" className="moe-link" onClick={() => onNavigate('/standings')}>
            {isTslhTenant()
              ? fr ? 'Voir mon classement' : 'View my standings'
              : fr ? 'Classement complet' : 'Full standings'}
          </button>
        }
      >
        {nearbyStandings.length === 0 ? (
          <p className="moe-empty">
            {fr ? 'Classement non disponible pour le moment.' : 'Standings unavailable for now.'}
          </p>
        ) : (
          <div className="moe-table-wrap">
            <table className="moe-table">
              <thead>
                <tr>
                  <th scope="col">{fr ? 'Pos' : 'Rank'}</th>
                  <th scope="col">{fr ? 'Équipe' : 'Team'}</th>
                  <th scope="col">{fr ? 'Points' : 'Points'}</th>
                </tr>
              </thead>
              <tbody>
                {nearbyStandings.map((row) => {
                  const isCurrent = Number(row.user_id) === Number(currentRow?.user_id);
                  return (
                    <tr key={`standing-${row.user_id}`} className={isCurrent ? 'is-me' : undefined}>
                      <td className="moe-table__rank">{row.rank || '—'}</td>
                      <td>
                        <div className="moe-table__team">
                          {row.nhl_team_abbrev && <TeamLogo abbrev={row.nhl_team_abbrev} size={24} />}
                          <span>{row.username}</span>
                        </div>
                      </td>
                      <td>{getPoints(row)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {!isBpmSession && (
        <Section
          title={fr ? 'Nouvelles' : 'News'}
          action={
            <a
              className="moe-link"
              href="https://www.toutsurlehockey.com/"
              target="_blank"
              rel="noreferrer noopener"
            >
              {fr ? 'Toutes les nouvelles' : 'All news'}
            </a>
          }
        >
          <LnhNewsSection compact />
        </Section>
      )}

      <div className="moe-push">
        <PushNotificationToggle />
      </div>

      {!isStandardFlexible && (
        <Section title={fr ? 'Quitter la ligue' : 'Leave league'}>
          <p className="moe-note" style={{ maxWidth: 720 }}>
            {translate('leave.league.description', language)}
          </p>
          <button
            type="button"
            className="moe-btn moe-btn--danger"
            disabled={isCommissioner}
            onClick={onOpenLeaveModal}
            title={translate('leave.league.cta.title', language)}
          >
            {translate('leave.league.button', language)}
          </button>
          <div className="moe-note" style={{ marginTop: '0.5rem' }}>
            {translate(
              isCommissioner ? 'leave.league.note.commissioner' : 'leave.league.note.default',
              language
            )}
          </div>
        </Section>
      )}

      {!isStandardFlexible && showLeaveModal && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{translate('leave.league.modal.title', language)}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  disabled={isLeavingLeague}
                  onClick={onCloseLeaveModal}
                />
              </div>
              <div className="modal-body">
                <p>{translate('leave.league.modal.body', language, { leagueName })}</p>
                <p className="text-danger small mb-0">
                  {translate('leave.league.modal.irreversible', language)}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="moe-btn moe-btn--outline"
                  disabled={isLeavingLeague}
                  onClick={onCloseLeaveModal}
                >
                  {translate('leave.league.modal.cancel', language)}
                </button>
                <button
                  type="button"
                  className="moe-btn moe-btn--danger"
                  disabled={isLeavingLeague}
                  onClick={onConfirmLeave}
                >
                  {isLeavingLeague ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {translate('leave.league.modal.progress', language)}
                    </>
                  ) : (
                    translate('leave.league.modal.confirm', language)
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </div>
      )}
    </div>
  );
}
